import { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import ApiUrl from "../Environment/ApiUrl";
import "../../assets/css/Bill.css";
import { useNavigate } from "react-router-dom";

export default function Purchasesmallbill() {
  const { purchase_id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchaseData, setPurchaseData] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (purchase_id) {
      fetchPurchaseData();
    } else {
      setError("Invalid purchase ID");
      setLoading(false);
    }
  }, [purchase_id]);

  const fetchPurchaseData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${ApiUrl}/Api/purchase_details.php/${purchase_id}`
      );

      if (!response.data || !response.data.data) {
        throw new Error("Invalid response format");
      }

      const filteredData = response.data.data.find(
        (item) => item.purchase_id === purchase_id
      );

      if (filteredData) {
        setPurchaseData(filteredData);
      } else {
        setError(`No purchase data found for purchase ID: ${purchase_id}`);
      }
    } catch (error) {
      console.error("API error:", error);
      setError(`Failed to fetch purchase data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

const numberToWords = (num, isPure = false) => {
  if (isNaN(num) || num < 0) return isPure ? "Zero Grams" : "Zero Rupees";

  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  const numToWord = (n) => {
    if (n === 0) return "";
    if (n < 20) return ones[n];
    if (n < 100)
      return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
    if (n < 1000)
      return (
        ones[Math.floor(n / 100)] +
        " Hundred" +
        (n % 100 ? " and " + numToWord(n % 100) : "")
      );
    if (n < 100000)
      return (
        numToWord(Math.floor(n / 1000)) +
        " Thousand" +
        (n % 1000 ? " " + numToWord(n % 1000) : "")
      );
    if (n < 10000000)
      return (
        numToWord(Math.floor(n / 100000)) +
        " Lakh" +
        (n % 100000 ? " " + numToWord(n % 100000) : "")
      );
    return (
      numToWord(Math.floor(n / 10000000)) +
      " Crore" +
        (n % 10000000 ? " " + numToWord(n % 10000000) : "")
    );
  };

  const decimalToWord = (decimalPart) => {
    if (decimalPart === 0) return "";
    const decStr = decimalPart.toString().padEnd(2, "0").slice(0, 2);
    const decValue = parseInt(decStr);
    
    if (decValue === 0) return "";
    
    // For Pure (grams), the decimal represents milligrams (hundredths of a gram)
    if (isPure) {
      if (decValue < 20) return " and " + ones[decValue] + " Milligram";
      return " and " + tens[Math.floor(decValue / 10)] + 
             (decValue % 10 ? " " + ones[decValue % 10] : "") + " Milligram";
    }
    
    // For Rupees, decimal represents paise
    if (decValue < 20) return " and " + ones[decValue] + " Paise";
    return " and " + tens[Math.floor(decValue / 10)] + 
           (decValue % 10 ? " " + ones[decValue % 10] : "") + " Paise";
  };

  const parts = num.toString().split(".");
  const integerPart = parseInt(parts[0]) || 0;
  const decimalPart = parts[1]
    ? parseInt(parts[1].padEnd(2, "0").slice(0, 2))
    : 0;

  let result = integerPart === 0 ? "Zero" : numToWord(integerPart);
  result += isPure ? " Gram" + (integerPart !== 1 ? "s" : "") : " Rupee" + (integerPart !== 1 ? "s" : "");
  
  if (decimalPart > 0) result += decimalToWord(decimalPart);

  return result;
};
  const calculatePureTotal = (itemList) => {
    if (!itemList || !Array.isArray(itemList)) return 0;
    return itemList.reduce((total, item) => {
      const weight = parseFloat(item.weight) || 0;
      const purity = parseFloat(item.percentage || item.purity) / 100 || 0.945;
      return total + weight * purity;
    }, 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return `${date.getDate().toString().padStart(2, "0")}-${(
        date.getMonth() + 1
      )
        .toString()
        .padStart(2, "0")}-${date.getFullYear()}`;
    } catch (e) {
      return dateString;
    }
  };

  if (loading)
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        Loading purchase data...
      </div>
    );
  if (error)
    return (
      <div style={{ textAlign: "center", padding: "40px", color: "red" }}>
        {error}
      </div>
    );
  if (!purchaseData)
    return (
      <div style={{ textAlign: "center", padding: "40px" }}>
        No purchase data available
      </div>
    );

  const totalQuantity =
    purchaseData.items?.reduce(
      (total, item) => total + (parseInt(item.quantity) || 0),
      0
    ) || 0;
  const totalWeight =
    purchaseData.items?.reduce(
      (total, item) => total + (parseFloat(item.weight) || 0),
      0
    ) || 0;
  const pureWeight = calculatePureTotal(purchaseData.items);

  // Calculate Total Paid based on return_type
  const totalPaidValue =
    purchaseData.return_type === "Amount"
      ? parseFloat(purchaseData.paid) || 0
      : parseFloat(purchaseData.pure) || 0;
  const totalPaidLabel = purchaseData.return_type === "Amount" ? "₹" : "g";
  const totalPaidInWords = numberToWords(
    totalPaidValue,
    purchaseData.return_type === "Pure"
  );

  const receiptData = {
    date: formatDate(purchaseData.purchase_date),
    party: purchaseData.supplier?.name || "SHEVAPET TIRUPPUR JEWELLERY",
    product_code: purchaseData.purchase_id,
    qty: totalQuantity,
    weight: totalWeight,
    purity: 94.5,
    fw: 0.0,
    pure: pureWeight,
    opening_pure: parseFloat(purchaseData.opening) || 0,
    current_pure: parseFloat(purchaseData.current) || 0,
    closing_pure: parseFloat(purchaseData.close) || 0,
    items: purchaseData.items || [],
    subTotal: parseFloat(purchaseData.total) || 0,
    discount: 0,
    netTotal: parseFloat(purchaseData.total) || 0,
    totalPaid: totalPaidValue,
    totalPaidLabel: totalPaidLabel,
    totalPaidInWords: totalPaidInWords,
    balance: parseFloat(purchaseData.balance) || 0,
  };

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  return (
    <div className="bill-container">
      <button className="print-btn no-print" onClick={() => window.print()}>
        Print Bill
      </button>
      <button
        className="cancel-btn no-print"
        onClick={() => navigate("/purchases")}
      >
        Cancel
      </button>

      {/* Header */}
      <div className="header-section row align-items-center">
        <div className="col-3 text-start">
          <img
            src={require("../../assets/images/Final logo c.png")}
            alt="TIRUPUR Logo"
            className="img-fluid"
            style={{ maxHeight: "80px" }}
          />
        </div>
        <div className="col-6 company-info text-center">
          <h4 className="mb-1">TIRUPUR JEWELLERY</h4>
          <p className="mb-0">
            3/1, Thandan St, Shevapet, Salem, Tamil Nadu 636002
          </p>
          <p className="mb-0">Ph: 04272233807</p>
        </div>
        <div className="col-3 company-info">
          <h4 className="text-dark">PURCHASE RECEIPT</h4>
        </div>
      </div>

      <hr className="divider" />

      {/* Purchase Details */}
      <div className="details-section">
        <div className="customer-info">
          <div className="customer-details">
            <p>
              <strong>{receiptData.party}</strong>
            </p>
            <p>Bill No: {receiptData.product_code}</p>
            <p>Date: {receiptData.date}</p>
          </div>
        </div>
        <div className="invoice-info">
          <div className="info-row">
            <span>Purchase ID</span>
            <span>: {receiptData.product_code}</span>
          </div>
          <div className="info-row">
            <span>Purchase Date</span>
            <span>: {receiptData.date}</span>
          </div>
          <div className="info-row">
            <span>Return Type</span>
            <span>: {purchaseData.return_type || "-"}</span>
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div className="table-section">
        <table className="bill-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Product</th>
              <th>Qty</th>
              <th>Weight (g)</th>
            </tr>
          </thead>
          <tbody>
            {receiptData.items.length === 0 ? (
              <tr>
                <td colSpan="4">No items found</td>
              </tr>
            ) : (
              receiptData.items.map((item, index) => (
                <tr key={item.id || index}>
                  <td>{index + 1}</td>
                  <td>{truncateText(item.item + " " + (item.type || ""), 20)}</td>
                  <td>{item.quantity || 0}</td>
                  <td>{(parseFloat(item.weight) || 0).toFixed(2)}g</td>
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            <tr className="total-row-table">
              <td colSpan="2">
                <strong>Total</strong>
              </td>
              <td>
                <strong>{receiptData.qty}</strong>
              </td>
              <td>
                <strong>{receiptData.weight.toFixed(2)}g</strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Pure Details and Payment Info */}
      <div className="details-section">
        <div className="customer-info">
          <div className="amount-in-words">
            <p>
              <strong>Amount in Words:</strong> {totalPaidInWords}
            </p>
          </div>
          <p className="paragraph-cont">
            * Goods once sold will not be taken back.
            <br />
            * This is a computer generated receipt.
          </p>
        </div>
        <div className="invoice-info">
          <div className="totals-section">
            <div className="totals-grid">
              <div className="total-row">
                <span>Opening Pure</span>
                <span>{receiptData.opening_pure.toFixed(2)}g</span>
              </div>
              <div className="total-row">
                <span>Current Pure</span>
                <span>{receiptData.current_pure.toFixed(2)}g</span>
              </div>
              <div className="total-row">
                <span>Closing Pure</span>
                <span>{receiptData.closing_pure.toFixed(2)}g</span>
              </div>
              <div className="total-row highlight">
                <span>Total Paid ({purchaseData.return_type === "Amount" ? "Amount" : "Pure"})</span>
                <span>
                  {receiptData.totalPaid.toFixed(2)}
                  {receiptData.totalPaidLabel}
                </span>
              </div>
              {receiptData.balance > 0 && (
                <div className="total-row">
                  <span>Balance</span>
                  <span>₹{receiptData.balance.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <hr className="divider" />

      {/* Footer */}
      <div className="footer-section">
        <div className="signatures">
          <div className="customer-sign">
            <p>Signature of the Supplier</p>
          </div>
          <div>
            <p className="footer-notes">--- Thank You ---</p>
            <div className="alaan">
              <p>Developed by Aalan Tech Soft - Salem</p>
            </div>
          </div>
          <div className="company-sign">
            <p>FOR TIRUPUR JEWELLERY</p>
            <p>Authorized Signature</p>
          </div>
        </div>
      </div>
    </div>
  );
}